import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Names users give shapes and QR codes. They're saved as `label`, for the
// editor only: `name` stays as created ("box-3", "line-4", "qr-1"), because
// it says what kind of shape an item is.

const SHAPE_KINDS = [["line", "Line"], ["circle", "Circle"], ["box", "Rectangle"]];

// "Rectangle 3" for "box-3", "QR code 1" for "qr-1".
export function defaultItemName(path, key) {
  const number = (String(key || "").match(/(\d+)$/) || [])[1];
  const kind = path === "qrCodes"
    ? "QR code"
    : (SHAPE_KINDS.find(([prefix]) => String(key || "").startsWith(prefix)) || [null, "Shape"])[1];
  return number ? `${kind} ${number}` : kind;
}

// The name to show for a shape or QR code: its label, or the default.
export function itemDisplayName(path, key, data) {
  const label = typeof data?.label === "string" ? data.label.trim() : "";
  return label || defaultItemName(path, key);
}

// A name with a pencil button that turns it into a text box. Enter or
// leaving the box saves, Esc cancels; saving it empty (or as the default)
// goes back to the default name. `onRename(label)`; undefined removes it.
export default function ElementName({ label, fallback, onRename, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const shown = (label || "").trim() || fallback;

  const commit = () => {
    setEditing(false);
    const next = text.trim();
    if (next === shown) return;
    onRename(next && next !== fallback ? next : undefined);
  };

  if (editing) {
    return (
      <input
        type="text"
        className={"form-control form-control-sm element-name-input " + className}
        value={text}
        maxLength={60}
        autoFocus
        onFocus={(e) => e.target.select()}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
      />
    );
  }

  return (
    <span className={"element-name " + className}>
      <span className="element-name-text">{shown}</span>
      <button
        type="button"
        className="element-name-edit"
        title="Rename"
        aria-label={`Rename ${shown}`}
        onClick={(e) => {
          e.stopPropagation();
          setText(shown);
          setEditing(true);
        }}
      >
        <FontAwesomeIcon icon="pen" />
      </button>
    </span>
  );
}
